import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { X, Lock } from "lucide-react";

export default function NotFound() {
  const navigate = useNavigate();
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminProcessing, setAdminProcessing] = useState(false);

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminError("");
    setAdminProcessing(true);

    // Simulate processing
    setTimeout(() => {
      if (adminId.trim() === "204715") {
        setShowAdminModal(false);
        setAdminId("");
        navigate("/admin");
      } else {
        setAdminError("Invalid Admin ID. Please try again.");
      }
      setAdminProcessing(false);
    }, 500);
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 text-white p-6 overflow-hidden">

      {/* Floating Animation Container */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, type: "spring" }}
        className="text-center z-10 max-w-2xl"
      >

        {/* 404 Number with gradient */}
        <motion.h1
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-8xl md:text-9xl font-extrabold bg-gradient-to-r from-red-500 via-amber-500 to-orange-500 bg-clip-text text-transparent drop-shadow-lg"
        >
          404
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-6 text-2xl md:text-3xl font-bold text-slate-100"
        >
          Page Not Found
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-3 text-lg text-slate-400 max-w-md mx-auto"
        >
          Sorry, the page you're looking for doesn't exist or has been moved.
        </motion.p>

        {/* Thank You */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-4 text-md text-emerald-400 font-medium"
        >
          Thank you for visiting our portal ❤️
        </motion.p>

        {/* ACTION BUTTONS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          {/* Customer Home */}
          <Link
            to="/"
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 transition shadow-lg text-white text-lg font-bold hover:shadow-blue-500/50 hover:shadow-xl"
          >
            🏠 Go Home
          </Link>

          {/* Agent Login */}
          <Link
            to="/agent/login"
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 transition shadow-lg text-white text-lg font-bold hover:shadow-purple-500/50 hover:shadow-xl"
          >
            🧑‍💼 Agent Login
          </Link>

          {/* Admin Button */}
          <button
            onClick={() => setShowAdminModal(true)}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 transition shadow-lg text-white text-lg font-bold hover:shadow-slate-500/50 hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Lock size={20} />
            Admin Portal
          </button>
        </motion.div>

      </motion.div>

      {/* Animated Glow Circles */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
        className="absolute bottom-20 right-20 w-80 h-80 bg-red-600/10 rounded-full blur-3xl pointer-events-none"
      ></motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute top-20 left-10 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"
      ></motion.div>

      {/* Admin ID Modal */}
      <AnimatePresence>
        {showAdminModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAdminModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
              <div className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 rounded-3xl shadow-2xl border border-slate-700 p-8">
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowAdminModal(false);
                    setAdminError("");
                    setAdminId("");
                  }}
                  className="absolute top-6 right-6 p-2 hover:bg-slate-700 rounded-full transition text-slate-300 hover:text-white"
                >
                  <X size={24} />
                </button>

                {/* Header */}
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="inline-block p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full mb-4"
                  >
                    <Lock size={32} className="text-white" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-white">Admin Portal</h2>
                  <p className="mt-2 text-slate-300">Enter your Admin ID to proceed</p>
                </div>

                {/* Form */}
                <form onSubmit={handleAdminSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-200 mb-2">
                      Admin ID
                    </label>
                    <input
                      type="password"
                      value={adminId}
                      onChange={(e) => {
                        setAdminId(e.target.value);
                        setAdminError("");
                      }}
                      placeholder="Enter Admin ID"
                      className="w-full px-5 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-lg font-mono"
                      disabled={adminProcessing}
                    />
                  </div>

                  {/* Error Message */}
                  {adminError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-red-900/20 border border-red-700 text-red-300 rounded-lg text-sm font-medium"
                    >
                      {adminError}
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={adminProcessing || !adminId.trim()}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:from-slate-600 disabled:to-slate-500 disabled:cursor-not-allowed transition font-bold text-white text-lg shadow-lg hover:shadow-amber-500/50 hover:shadow-xl"
                  >
                    {adminProcessing ? "Verifying..." : "Enter Portal"}
                  </button>
                </form>

                {/* Helper Text */}
                <p className="mt-6 text-center text-xs text-slate-400">
                  Contact support if you don't have your Admin ID
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
